import {
  useLocation,
  useNavigate,
  Link
} from "react-router-dom";
import {
  useState,
  useEffect,
  useRef
} from "react";
import {
  verifyOtp,
  signup
} from "../api/emailAuthApi";
import {
  useAuth
} from "../context/AuthContext";
import {
  School,
  Check,
  ArrowRight,
  RotateCcw,
  HelpCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Lock
} from "lucide-react";
import {
  useToast
} from "../context/ToastContext";

const VerifyOtp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  // Extract router context values passed down from prior registration steps
  const { name, email, password: initialPassword } = location.state || {};

  // Form State Vectors
  const [otpArray, setOtpArray] = useState(new Array(6).fill(""));
  const [password, setPassword] = useState(initialPassword || "");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // UI Interactive States (Timer & Action State)
  const [timeLeft, setTimeLeft] = useState(119); // 1 minute 59 seconds initial value
  const [canResend, setCanResend] = useState(false);

  // References array container pointing explicitly to native elements
  const inputRefs = useRef([]);

  // Auto-focus first cell on load hook configuration
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Countdown timer clock interval scheduler
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }
    const timerStream = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timerStream);
  }, [timeLeft]);

  // Formats remaining integer seconds into digital semantic timestamp format
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Strategic linked interaction processing node handler
  const handleOtpChange = (element, index) => {
    const rawVal = element.value.replace(/[^0-9]/g, ""); // Allow numeric entries only
    if (!rawVal) return;

    const updatedOtp = [...otpArray];
    updatedOtp[index] = rawVal.target ? rawVal.target.value : rawVal.substring(rawVal.length - 1);
    setOtpArray(updatedOtp);

    // Context aware logical step traversal shift right rule execution
    if (index < 5 && updatedOtp[index] !== "") {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const updatedOtp = [...otpArray];
      
      if (otpArray[index] !== "") {
        // Clear current active field data
        updatedOtp[index] = "";
        setOtpArray(updatedOtp);
      } else if (index > 0) {
        // Move backward and clear preceding element input value container 
        updatedOtp[index - 1] = "";
        setOtpArray(updatedOtp);
        inputRefs.current[index - 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim().replace(/[^0-9]/g, "").slice(0, 6);
    
    if (pastedData.length === 6) {
      const splitDigits = pastedData.split("");
      setOtpArray(splitDigits);
      inputRefs.current[5].focus();
    }
  };

  const handleResendAction = async () => {
    if (!canResend) return;
    try {
      // Add alternative API resend verification handler invocation logic here if available
      setTimeLeft(119);
      setCanResend(false);
    } catch (err) {
      toast.error("Failed to dispatch a fresh verification pass. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const compiledOtp = otpArray.join("");

    if (compiledOtp.length !== 6) {
      toast.error("Please supply the complete 6-digit authentication token string.");
      return;
    }

    try {
      setLoading(true);

      // Step A: Finalize verification layer processing rules
      await verifyOtp(email || "user@school.edu", compiledOtp);

      // Step B: Formally register institutional security payload profiles
      await signup({
        name: name || "Administrator Account",
        email: email || "user@school.edu",
        password: password,
      });

      // Step C: Update local routing state and user authorization scopes
      const authData = await refreshUser();

      if (authData?.hasSchool) {
        navigate("/school-home");
      } else {
        navigate("/school-setup");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Verification chain processing error. Ensure token is still active."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-background text-on-surface font-sans min-h-screen flex flex-col antialiased selection:bg-primary-fixed selection:text-on-primary-fixed">
      
      {/* Top Application Header Bar */}
      <header class="bg-white dark:bg-neutral-900 border-b border-outline-variant h-16 w-full flex items-center px-6 md:px-10 shadow-sm fixed top-0 z-50">
        <div className="flex justify-between items-center w-full max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <School className="text-primary w-7 h-7" />
            <span className="font-headline-md text-xl md:text-2xl font-bold text-primary tracking-tight">
              EduClarify ERP
            </span>
          </div>
          <button className="text-on-surface-variant hover:bg-surface-container-low p-2 rounded-full transition-all duration-200">
            <HelpCircle className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Main Form Content Canvas */}
      <main className="flex-grow flex items-center justify-center pt-24 pb-12 px-4 md:px-10">
        <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          
          {/* Decorative Brand Collateral Panel Side Presentation */}
          <div className="hidden md:flex flex-col gap-8 pr-12">
            <div className="relative w-full aspect-square rounded-3xl overflow-hidden bg-primary-container/10 border border-primary/20 flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-container/20 to-transparent"></div>
              <div className="z-10 text-center px-8 animate-float">
                <School className="w-24 h-24 text-primary mx-auto mb-4 opacity-80" />
                <h3 className="font-title-lg text-xl text-on-background font-bold mb-2">Secure Institutional Access</h3>
                <p className="text-body-md text-on-surface-variant max-w-xs">
                  A unified framework keeping digital learning environments robustly managed.
                </p>
              </div>
            </div>
          </div>

          {/* OTP Handling Section Interface Element */}
          <div className="flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            
            {/* Multi-Step Linear Progress Bar Tracker */}
            <div className="flex items-center justify-between w-full mb-4">
              <div className="flex flex-col items-center gap-2 relative flex-1">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm shadow-sm">
                  <Check className="w-4 h-4" />
                </div>
                <span className="font-label-md text-label-md text-primary font-medium">Account</span>
                <div className="absolute top-4 left-1/2 w-full h-[2px] bg-primary z-0" />
              </div>
              
              <div className="flex flex-col items-center gap-2 relative flex-1">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm shadow-md ring-4 ring-primary/10 z-10">
                  2
                </div>
                <span className="font-label-md text-label-md text-primary font-bold">Verify</span>
                <div className="absolute top-4 left-1/2 w-full h-[2px] bg-outline-variant z-0" />
              </div>
              
              <div className="flex flex-col items-center gap-2 relative flex-1">
                <div className="w-8 h-8 rounded-full bg-surface-container-highest text-on-surface-variant flex items-center justify-center font-bold text-sm z-10">
                  3
                </div>
                <span className="font-label-md text-label-md text-on-surface-variant font-medium">Profile</span>
                <div className="absolute top-4 left-1/2 w-full h-[2px] bg-outline-variant z-0" />
              </div>
              
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-surface-container-highest text-on-surface-variant flex items-center justify-center font-bold text-sm">
                  4
                </div>
                <span className="font-label-md text-label-md text-on-surface-variant font-medium">Finish</span>
              </div>
            </div>

            {/* Core Segmented Form Wrapper Card */}
            <div className="bg-white dark:bg-neutral-900 border border-outline-variant rounded-xl p-8 md:p-10 shadow-sm">
              <div className="mb-8">
                <h1 className="font-headline-md text-2xl md:text-3xl font-bold text-on-surface mb-2">Verify your account</h1>
                <p className="font-body-md text-body-md text-on-surface-variant">
                  Please enter the 6-digit code dispatched to{" "}
                  <span className="font-semibold text-on-surface">{email || "+1 (•••) •••-4521"}</span>
                </p>
              </div>

              {/* Dynamic Error Status Diagnostic Field Block */}
              

              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* 6-Digit Flex Box Layout Container */}
                <div className="flex gap-2 md:gap-3 justify-between mb-6" onPaste={handlePaste}>
                  {otpArray.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (inputRefs.current[idx] = el)}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(e.target, idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      className="w-full h-14 md:h-16 text-center text-2xl font-bold border border-outline-variant rounded-lg bg-surface-container-low focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all duration-150"
                    />
                  ))}
                </div>

                {/* Password Setting Access Control Layer Field (Optional fallback protection mechanism) */}
                {!initialPassword && (
                  <div className="space-y-2 mb-4">
                    <label className="block font-label-md text-label-md text-on-surface-variant font-semibold" htmlFor="password">
                      Establish System Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-outline group-focus-within:text-primary transition-colors">
                        <Lock className="w-5 h-5" />
                      </div>
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full pl-11 pr-11 py-2.5 bg-surface-container-lowest border border-outline-variant rounded-lg font-body-md text-body-md text-on-surface focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-outline hover:text-on-surface transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Primary System Action Dispatch Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-on-primary font-title-md text-title-md py-4 rounded-lg hover:bg-primary-container active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-md disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  <span>{loading ? "Verifying..." : "Verify OTP"}</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>

              {/* Dynamic Resend Token Controller Operations Layer */}
              <div className="flex flex-col items-center gap-4 mt-6">
                {!canResend ? (
                  <div className="flex items-center gap-2 text-on-surface-variant font-body-md">
                    <span>Didn't receive the code?</span>
                    <span className="font-bold text-primary tracking-wide">{formatTime(timeLeft)}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendAction}
                    className="text-primary font-title-md text-title-md hover:text-primary-container font-semibold transition-colors flex items-center gap-1.5 animate-in fade-in zoom-in duration-300"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Resend OTP Code
                  </button>
                )}
              </div>
            </div>

            {/* Legal Consent Acknowledgements Context Block */}
            <p className="text-center font-label-md text-label-md text-on-surface-variant px-4 leading-relaxed">
              By verifying, you agree to our{" "}
              <Link to="/terms" className="text-primary font-medium hover:underline">Terms of Service</Link> and{" "}
              <Link to="/privacy" className="text-primary font-medium hover:underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </main>

      {/* Global Interface Sticky Institutional Footer Layout */}
      <footer className="mt-auto py-6 px-6 md:px-10 border-t border-outline-variant bg-white dark:bg-neutral-900">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="font-label-md text-label-md text-on-surface-variant opacity-70 text-center sm:text-left">
            © {new Date().getFullYear()} TFC elevate. All institutional data secured.
          </span>
          <div className="flex gap-6">
            <a className="font-label-md text-label-md text-on-surface-variant hover:text-primary transition-colors" href="#status">
              System Status
            </a>
            <a className="font-label-md text-label-md text-on-surface-variant hover:text-primary transition-colors" href="#support">
              Contact Support
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VerifyOtp;